import { useCallback, useState, type ReactNode } from 'react'
import { Upload } from 'lucide-react'

// OSPROD-75: drag-over visual + drop-handoff for a channel chat region
// (text channel OR in-call voice chat tab). Originally this wrapper
// also ran the upload + socket emit inline, which meant DnD skipped the
// OSPROD-78 preview+caption modal entirely — the user couldn't see the
// thumbnail or type a caption before sending. We now hand the dropped
// files to the composer via `onFilesDropped`, so DnD and the paperclip
// button land in the same modal.
export default function ChannelDropZone({
  onFilesDropped,
  children,
}: {
  onFilesDropped: (files: File[]) => void
  children: ReactNode
}) {
  const [isDragOver, setIsDragOver] = useState(false)

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only clear when the cursor truly leaves the wrapper, not when it
    // moves between inner children (those fire dragleave too).
    if (e.currentTarget.contains(e.relatedTarget as Node)) return
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length === 0) return
    onFilesDropped(files)
  }, [onFilesDropped])

  return (
    <div
      // OSPROD-80: min-h-0 is critical here — without it, the min-content
      // height of TelegramMessageInput (reply preview + voice "Кому"
      // picker + auto-grown textarea) cascades through `min-height: auto`
      // and pushes the input below the side-panel viewport when this
      // wrapper is used inside the h-72 voice side panel.
      className="flex-1 flex flex-col min-w-0 min-h-0 bg-background relative"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
      {isDragOver && (
        <div className="absolute inset-0 bg-primary/10 border-2 border-dashed border-primary z-20 flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center gap-2 text-primary">
            <Upload size={32} />
            <span className="font-medium">Drop file to upload</span>
          </div>
        </div>
      )}
    </div>
  )
}
